import { useMemo, useState } from 'react';
import {
  Button,
  ConfirmDialog,
  DataTable,
  Group,
  Modal,
  PageHeader,
  SearchBar,
  Stack,
  TextInput,
  type DataTableColumn,
} from '@vipso/shared';
import {
  useAddUpdateMasterRoleMutation,
  useDeleteMasterRoleMutation,
  useGetMasterRolesQuery,
  type MasterRole,
} from '../api/adminApi';

export function RolesPage() {
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<MasterRole | null>(null);
  const [name, setName] = useState('');
  const [pendingDelete, setPendingDelete] = useState<MasterRole | null>(null);

  const { data, isLoading } = useGetMasterRolesQuery();
  const [saveRole, saveState] = useAddUpdateMasterRoleMutation();
  const [deleteRole, deleteState] = useDeleteMasterRoleMutation();

  const rows = useMemo(() => {
    const list = data?.masterRoles ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) => r.name.toLowerCase().includes(q));
  }, [data?.masterRoles, search]);

  const columns: DataTableColumn<MasterRole>[] = [
    { key: 'id', header: 'ID', render: (row) => row.id },
    { key: 'name', header: 'Role name', render: (row) => row.name },
    { key: 'roleFor', header: 'For', render: (row) => row.roleFor },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <Group gap="xs">
          <Button
            size="xs"
            variant="default"
            onClick={() => {
              setEditing(row);
              setName(row.name);
              setModalOpen(true);
            }}
          >
            Edit
          </Button>
          <Button size="xs" color="red" variant="light" onClick={() => setPendingDelete(row)}>
            Delete
          </Button>
        </Group>
      ),
    },
  ];

  async function handleSave() {
    await saveRole({
      ...(editing ? { id: editing.id } : {}),
      name: name.trim(),
    }).unwrap();
    setModalOpen(false);
    setEditing(null);
    setName('');
  }

  return (
    <>
      <PageHeader
        title="Master roles"
        description="System-owner roles for platform staff."
        actions={
          <Button
            onClick={() => {
              setEditing(null);
              setName('');
              setModalOpen(true);
            }}
          >
            Add role
          </Button>
        }
      />

      <SearchBar value={search} onChange={setSearch} placeholder="Search roles..." />

      <DataTable
        columns={columns}
        rows={rows}
        loading={isLoading}
        getRowId={(row) => row.id}
        emptyTitle="No roles yet"
        emptyDescription="Create a master role to assign to staff."
      />

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit role' : 'Add role'}
      >
        <Stack gap="md">
          <TextInput
            label="Role name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button loading={saveState.isLoading} disabled={!name.trim()} onClick={() => void handleSave()}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <ConfirmDialog
        opened={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={async () => {
          if (!pendingDelete) return;
          await deleteRole({ id: pendingDelete.id }).unwrap();
          setPendingDelete(null);
        }}
        title="Delete role"
        message={pendingDelete ? `Delete role "${pendingDelete.name}"?` : ''}
        confirmLabel="Delete"
        loading={deleteState.isLoading}
        danger
      />
    </>
  );
}
